import { Table } from "@4uhub/lib4uhub";
import DataObjectIcon from "@mui/icons-material/DataObject";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridEventListener, GridRowParams } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ICalendarControlXl } from "../../../../models/calendar-control";
import MainApi from "../../../../services/mainApi.service";
import CalendarControlDetailsModal from "./CalendarControlDetailsModal";
import CalendarControlItemsModal from "./CalendarControlItemsModal";
import CalendarControlJsonModal from "./CalendarControlJsonModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import {
  CALENDAR_CONTROL_ROUTE,
  formatDate,
  formatDateTime,
  formatDocument,
  listStatusConfig,
  toDateParam,
  translationPath,
} from "./utils";

const calendarControlService = new MainApi<ICalendarControlXl>(CALENDAR_CONTROL_ROUTE);

type TModal = "json" | "details" | "items";

const CalendarControlPage = () => {
  const [refresh, setRefresh] = useState(0);
  const [openModal, setOpenModal] = useState<TModal | null>(null);
  const [selectedRow, setSelectedRow] = useState<ICalendarControlXl | null>(null);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Document: "",
    CreatedStart: null,
    CreatedEnd: null,
    TypeOfAppointment: "",
    Status: "",
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const label = useCallback(
    (key: string, fallback: string) => t(translationPath + key) || fallback,
    [t],
  );

  const open = useCallback((modal: TModal, row: ICalendarControlXl) => {
    setSelectedRow(row);
    setOpenModal(modal);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(null);
    setSelectedRow(null);
  }, []);

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      onRefreshHandler();
    },
    [onRefreshHandler],
  );

  const handleRowClick = useCallback<GridEventListener<"rowClick">>(
    (params, event) => {
      // Cliques no menu "…" (inclusive no popover, que borbulha pelo portal) não abrem os horários.
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-field="actions"], [role="menu"], .MuiPopover-root')) return;
      open("items", params.row);
    },
    [open],
  );

  const columns: GridColDef<ICalendarControlXl>[] = useMemo(
    () => [
      {
        field: "identifier",
        headerName: label("identifier", "Identificador"),
        flex: 1,
        minWidth: 120,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => params.value ?? "-",
      },
      {
        field: "created",
        headerName: label("created", "Data de criação"),
        flex: 1,
        minWidth: 150,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDateTime(params.value, language),
      },
      {
        field: "document",
        headerName: label("document", "CPF"),
        flex: 1,
        minWidth: 140,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDocument(params.value),
      },
      {
        field: "birthDate",
        headerName: label("birth_date", "Data de nascimento"),
        flex: 1,
        minWidth: 150,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => formatDate(params.value, language),
      },
      {
        field: "typeOfAppointment",
        headerName: label("type_of_appointment", "Tipo de agenda"),
        flex: 1,
        minWidth: 130,
        align: "left",
        headerAlign: "left",
        renderCell: (params) =>
          params.value
            ? t(translationPath + `type_of_appointment_values.${params.value}`) || params.value
            : "-",
      },
      {
        field: "status",
        headerName: label("status", "Status da lista"),
        flex: 1,
        minWidth: 160,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (!params.value) return "-";
          const cfg = listStatusConfig[params.value];
          return (
            <Chip
              label={cfg ? t(translationPath + `status_values.${cfg.key}`) : params.value}
              color={cfg?.color ?? "default"}
              size="small"
            />
          );
        },
      },
      {
        field: "jsonContent",
        headerName: label("json", "JSON"),
        width: 90,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={label("view_json", "Ver JSON da requisição")}>
            <span>
              <IconButton
                size="small"
                disabled={!params.value}
                onClick={(e) => {
                  e.stopPropagation();
                  open("json", params.row);
                }}
              >
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [label, language, open, t],
  );

  const rowActions = useCallback(
    (params: GridRowParams<ICalendarControlXl>) => [
      {
        action: () => open("details", params.row),
        icon: <VisibilityIcon fontSize="small" />,
        text: label("view_details", "Ver detalhes"),
      },
    ],
    [label, open],
  );

  const serviceProps = useMemo(
    () => ({
      refresh,
      Document: filterParams.Document || undefined,
      TypeOfAppointment: filterParams.TypeOfAppointment || undefined,
      Status: filterParams.Status || undefined,
      CreatedStart: toDateParam(filterParams.CreatedStart),
      CreatedEnd: toDateParam(filterParams.CreatedEnd),
    }),
    [refresh, filterParams],
  );

  return (
    <>
      {openModal === "json" && selectedRow && (
        <CalendarControlJsonModal calendarControl={selectedRow} onClose={handleCloseModal} />
      )}
      {openModal === "details" && selectedRow && (
        <CalendarControlDetailsModal calendarControl={selectedRow} onClose={handleCloseModal} />
      )}
      {openModal === "items" && selectedRow && (
        <CalendarControlItemsModal calendarControl={selectedRow} onClose={handleCloseModal} />
      )}

      {/* A lib fixa checkboxSelection na Table e não expõe prop para desligar; escondemos a coluna por CSS. */}
      <Box
        sx={{
          height: "100%",
          // !important: as regras do próprio DataGrid têm a mesma especificidade e prevalecem sem ele.
          "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
            display: "none !important",
          },
        }}
      >
        <Table<ICalendarControlXl>
          title={t("components.menu.4uChatBot.logs.calendarControl")}
          columns={columns}
          service={calendarControlService}
          searchInput={false}
          searchInputPlaceHolder=""
          showDefaultMacroFilters={false}
          showButton={false}
          showDeleteButton={false}
          defaultActions={true}
          rowActions={rowActions}
          hideLog
          getRowId={(row) => row.identifier ?? `${row.document}-${row.created}`}
          onRowClick={handleRowClick}
          defaultPageSize={100}
          pageSizeOptions={[100, 200, 500]}
          serviceProps={serviceProps}
          macroFilters={useCallback(
            () => <MacroFilter filterParams={filterParams} onFilterParams={handleFilterChange} />,
            [filterParams, handleFilterChange],
          )}
        />
      </Box>
    </>
  );
};

export default CalendarControlPage;
